import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  Settings,
  FileText,
  Search,
  ScanText,
  Loader2,
  CheckCircle,
  Clock,
  Building2,
  ClipboardList,
  X,
  Sparkles,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'menu.dashboard.view' },
  { name: 'Gefangene', href: '/prisoners', icon: Users, permission: 'menu.prisoners.view' },
  { name: 'Vorgänge (VGO)', href: '/vgo-workflows', icon: ClipboardList, permission: 'menu.workflows.view' },
  { name: 'Suche', href: '/search', icon: Search, permission: 'menu.prisoners.view' },
  { name: 'KI-Assistent', href: '/ai-assistant', icon: Sparkles, permission: 'menu.ai-assistant.view' },
];

// Leitung-spezifische Navigation
const leadershipNavigation = [
  { name: 'Dashboard Anstaltsleitung', href: '/dashboard/management', icon: Building2, permission: 'menu.management-dashboard.view' },
];

const adminNavigation = [
  { name: 'Benutzer & Rollen', href: '/admin/users', icon: Users, permission: 'admin.users.view' },
  { name: 'Audit-Log', href: '/admin/audit', icon: FileText, permission: 'admin.audit.view' },
  { name: 'Verwaltung', href: '/admin', icon: Settings, permission: 'menu.admin.view' },
];

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface SidebarProps {
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export function Sidebar({ mobileOpen = false, onCloseMobile }: SidebarProps) {
  const location = useLocation();
  const { hasPermission, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [lastJobResult, setLastJobResult] = useState<{ queued: number } | null>(null);

  const isAdmin = hasPermission('menu.admin.view');
  const isLeadership = hasPermission('menu.management-dashboard.view');

  // OCR Status Query
  const { data: ocrStatus } = useQuery({
    queryKey: ['ocr-status-sidebar'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/documents/ocr-status`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) return null;
      return response.json() as Promise<Record<string, number>>;
    },
    enabled: isAuthenticated,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // OCR Batch Job Mutation
  const ocrBatchMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/documents/reprocess-ocr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('OCR-Verarbeitung konnte nicht gestartet werden');
      return response.json();
    },
    onSuccess: (data) => {
      setLastJobResult({ queued: data.queued || 0 });
      toast({
        title: 'OCR-Batch gestartet',
        description: `${data.queued || 0} Dokumente zur Verarbeitung eingereiht.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const pendingCount = ocrStatus?.PENDING || 0;
  const processingCount = ocrStatus?.PROCESSING || 0;
  const handleNavigationClick = () => {
    if (mobileOpen) {
      onCloseMobile?.();
    }
  };

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          aria-label="Navigation schließen"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={onCloseMobile}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 flex-shrink-0 border-r bg-card transition-transform duration-200 ease-out lg:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between border-b px-6">
            <Link to="/" className="flex items-center gap-3" onClick={handleNavigationClick}>
              <img 
                src="/egpa-logo.png" 
                alt="eGPA" 
                className="h-12 w-auto"
              />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={onCloseMobile}
              title="Navigation schließen"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
            <div className="space-y-1">
              {navigation.filter((item) => hasPermission(item.permission)).map((item) => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleNavigationClick}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Anstaltsleitung Section */}
            {isLeadership && (
              <>
                <div className="my-4 border-t" />
                <div className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Anstaltsleitung
                </div>
                <div className="mt-2 space-y-1">
                  {leadershipNavigation.filter((item) => hasPermission(item.permission)).map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={handleNavigationClick}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </>
            )}

            {/* OCR Batch Section */}
            {isAuthenticated && (
              <>
                <div className="my-4 border-t" />
                <div className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  OCR-Verarbeitung
                </div>
                <div className="mt-2 space-y-2 px-3">
                  {/* Status Display */}
                  {ocrStatus && (pendingCount > 0 || processingCount > 0) && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {processingCount > 0 && (
                        <span className="flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                          {processingCount} in Bearbeitung
                        </span>
                      )}
                      {pendingCount > 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-amber-500" />
                          {pendingCount} wartend
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* OCR Batch Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start gap-2"
                    onClick={() => ocrBatchMutation.mutate()}
                    disabled={ocrBatchMutation.isPending}
                  >
                    {ocrBatchMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ScanText className="h-4 w-4" />
                    )}
                    {ocrBatchMutation.isPending ? 'Wird gestartet...' : 'OCR-Batch starten'}
                  </Button>

                  {/* Last Job Result */}
                  {lastJobResult && !ocrBatchMutation.isPending && (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle className="h-3 w-3" />
                      {lastJobResult.queued} Dokumente eingereiht
                    </div>
                  )}
                </div>
              </>
            )}

            {isAdmin && (
              <>
                <div className="my-4 border-t" />
                <div className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Administration
                </div>
                <div className="mt-2 space-y-1">
                  {adminNavigation.filter((item) => hasPermission(item.permission)).map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={handleNavigationClick}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="border-t p-4 text-center text-xs text-muted-foreground">
            <p>eGPA NRW v0.3.0</p>
            <p className="mt-1">Justizvollzug NRW</p>
          </div>
        </div>
      </aside>

      <aside className="hidden w-64 flex-shrink-0 border-r bg-card lg:block">
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center border-b px-6">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/egpa-logo.png" 
                alt="eGPA" 
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
            <div className="space-y-1">
              {navigation.filter((item) => hasPermission(item.permission)).map((item) => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Anstaltsleitung Section */}
            {isLeadership && (
              <>
                <div className="my-4 border-t" />
                <div className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Anstaltsleitung
                </div>
                <div className="mt-2 space-y-1">
                  {leadershipNavigation.filter((item) => hasPermission(item.permission)).map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </>
            )}

            {/* OCR Batch Section */}
            {isAuthenticated && (
              <>
                <div className="my-4 border-t" />
                <div className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  OCR-Verarbeitung
                </div>
                <div className="mt-2 space-y-2 px-3">
                  {/* Status Display */}
                  {ocrStatus && (pendingCount > 0 || processingCount > 0) && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {processingCount > 0 && (
                        <span className="flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                          {processingCount} in Bearbeitung
                        </span>
                      )}
                      {pendingCount > 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-amber-500" />
                          {pendingCount} wartend
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* OCR Batch Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start gap-2"
                    onClick={() => ocrBatchMutation.mutate()}
                    disabled={ocrBatchMutation.isPending}
                  >
                    {ocrBatchMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ScanText className="h-4 w-4" />
                    )}
                    {ocrBatchMutation.isPending ? 'Wird gestartet...' : 'OCR-Batch starten'}
                  </Button>

                  {/* Last Job Result */}
                  {lastJobResult && !ocrBatchMutation.isPending && (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle className="h-3 w-3" />
                      {lastJobResult.queued} Dokumente eingereiht
                    </div>
                  )}
                </div>
              </>
            )}

            {isAdmin && (
              <>
                <div className="my-4 border-t" />
                <div className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Administration
                </div>
                <div className="mt-2 space-y-1">
                  {adminNavigation.filter((item) => hasPermission(item.permission)).map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="border-t p-4 text-center text-xs text-muted-foreground">
            <p>eGPA NRW v0.3.0</p>
            <p className="mt-1">Justizvollzug NRW</p>
          </div>
        </div>
      </aside>
    </>
  );
}
